import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import {
  LayoutDashboard,
  Target,
  Users,
  TrendingUp,
  FileText,
  LogOut,
  Menu,
  User,
  Award,
  X,
  UserCircle,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState, useEffect } from 'react';

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  :root {
    --c:      #FAF8F4; --c2: #F2EDE3; --c3: #E6DFD1;
    --forest: #1C3830; --fmid: #2B5244;
    --gold:   #C09A40; --gold-bg: #F5EECB;
    --stone:  #796F61; --ink: #18150F; --white: #FFFFFF;
  }
  .fl * { box-sizing: border-box; }
  .fl { font-family: 'DM Sans', sans-serif; color: var(--ink); background: var(--c); }
  .fl-scroll::-webkit-scrollbar { width: 3px; }
  .fl-scroll::-webkit-scrollbar-thumb { background: var(--c3); border-radius: 99px; }

  .fl-aside {
    width: 252px; flex-shrink: 0;
    background: var(--c); border-right: 1px solid var(--c3);
    display: flex; flex-direction: column;
  }
  @media (max-width: 767px) { .fl-aside { display: none !important; } }

  .fl-header {
    height: 60px; flex-shrink: 0;
    background: rgba(250,248,244,0.93);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--c3);
    display: flex; align-items: center;
    justify-content: space-between;
    padding: 0 20px; z-index: 20;
  }

  .fl-brand {
    height: 60px; display: flex; align-items: center;
    padding: 0 18px; border-bottom: 1px solid var(--c3);
    gap: 10px; flex-shrink: 0;
  }
  .fl-mark {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--forest); display: flex;
    align-items: center; justify-content: center; flex-shrink: 0;
  }
  .fl-brandname {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: 14.5px; color: var(--forest);
    letter-spacing: -.02em; line-height: 1.2;
  }
  .fl-squad-tag {
    margin-left: auto;
    background: var(--gold-bg); color: var(--gold);
    font-size: 10px; font-weight: 700;
    letter-spacing: .05em; text-transform: uppercase;
    border-radius: 99px; padding: 3px 9px;
    border: 1px solid rgba(192,154,64,.3);
    white-space: nowrap; max-width: 110px;
    overflow: hidden; text-overflow: ellipsis;
  }

  .fl-link {
    display: flex; align-items: center; gap: 9px;
    padding: 7.5px 11px; border-radius: 9px;
    font-size: 13.5px; font-weight: 450;
    color: var(--stone); text-decoration: none;
    transition: color .13s, background .13s;
    position: relative; letter-spacing: -.01em;
  }
  .fl-link:hover { color: var(--forest); background: var(--c2); }
  .fl-link.on    { color: var(--forest); background: var(--c2); font-weight: 560; }
  .fl-link.on::after {
    content: ''; position: absolute;
    left: 0; top: 20%; height: 60%; width: 3px;
    background: var(--gold); border-radius: 0 3px 3px 0;
  }

  .fl-ibtn {
    width: 34px; height: 34px; border-radius: 8px;
    border: 1px solid var(--c3); background: var(--white);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all .13s;
    color: var(--stone); position: relative; flex-shrink: 0;
  }
  .fl-ibtn:hover { background: var(--c2); color: var(--forest); }

  .fl-av {
    border-radius: 50%;
    background: var(--gold-bg); color: var(--gold);
    font-weight: 650; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; border: 1.5px solid var(--c3);
    overflow: hidden; cursor: pointer;
    transition: border-color .13s;
  }
  .fl-av:hover { border-color: var(--gold); }

  .fl-ucard {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: 9px;
    transition: background .13s; cursor: default;
  }
  .fl-ucard:hover { background: var(--c2); }

  .fl-hamburger { display: flex; }
  @media (min-width: 768px) { .fl-hamburger { display: none !important; } }

  .fl-mobile-title { display: block; }
  @media (min-width: 768px) { .fl-mobile-title { display: none !important; } }

  .fl-dd {
    background: var(--white) !important;
    border: 1px solid var(--c3) !important;
    border-radius: 13px !important;
    box-shadow: 0 10px 40px rgba(28,56,48,.10) !important;
    padding: 6px !important;
  }
  .fl-di {
    border-radius: 7px !important; font-size: 13.5px !important;
    color: var(--ink) !important; padding: 8px 10px !important;
    gap: 9px !important; cursor: pointer !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .fl-di:hover, .fl-di:focus { background: var(--c2) !important; color: var(--forest) !important; }
  .fl-di.red { color: #B83232 !important; }
  .fl-di.red:hover { background: #FEF0EE !important; color: #B83232 !important; }
`;

function ForgeStyles() {
  useEffect(() => {
    const id = 'fl-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = STYLES;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

function ForgeMark() {
  return (
    <div className="fl-mark">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 2C8 2 11 5.5 11 8.5C11 10.4 9.7 12 8 12C6.3 12 5 10.4 5 8.5C5 7 6 5.5 6 5.5C6 5.5 6.5 7 7.5 7C7.5 7 6.5 4.5 8 2Z"
          fill="#C09A40"
          opacity="0.9"
        />
        <path
          d="M8 9C8 9 9 8 9 8.8C9 9.5 8.6 10 8 10C7.4 10 7 9.5 7 8.8C7 8 8 9 8 9Z"
          fill="#F5EECB"
        />
      </svg>
    </div>
  );
}

const NAV_ITEMS = [
  { path: '/forge/dashboard',    label: 'Dashboard',    icon: LayoutDashboard },
  { path: '/forge/problem-bank', label: 'Problem Bank', icon: Target          },
  { path: '/forge/squad',        label: 'Squad',        icon: Users           },
  { path: '/forge/progress',     label: 'Progress',     icon: TrendingUp      },
  { path: '/forge/loi-tracker',  label: 'LOIs',         icon: Award           },
  { path: '/forge/resources',    label: 'Resources',    icon: FileText        },
];

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const active = location.pathname;
  
  const isActive = (path: string) => {
    if (path === '/forge/squad') {
      return active === path || active.startsWith('/forge/squad/');
    }
    return active === path;
  };
  
  return (
    <nav style={{ display:'flex', flexDirection:'column', gap:2, padding:'10px 9px' }}>
      {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
        <Link
          key={path}
          to={path}
          className={`fl-link${isActive(path) ? ' on' : ''}`}
          onClick={onNavigate}
        >
          <Icon size={15} strokeWidth={1.7} />
          {label}
        </Link>
      ))}
    </nav>
  );
}

export default function ForgeLayout() {
  const { squadUser, logout } = useSquadAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/forge/auth'); };

  const initials = (name: string) =>
    name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <>
      <ForgeStyles />
      <div className="fl" style={{ display:'flex', height:'100vh', width:'100%', overflow:'hidden' }}>

        {/* Desktop Sidebar */}
        <aside className="fl-aside">
          <div className="fl-brand">
            <ForgeMark />
            <span className="fl-brandname">The Forge</span>
            {squadUser && (
              <span className="fl-squad-tag">{squadUser.squadName}</span>
            )}
          </div>

          <div className="fl-scroll" style={{ flex:1, overflowY:'auto' }}>
            <NavLinks />
          </div>

          {/* User card - Shows logged-in member */}
          {squadUser && (
            <div style={{ borderTop:'1px solid var(--c3)', padding:'11px 9px', flexShrink:0 }}>
              <div className="fl-ucard">
                <div className="fl-av" style={{ width:34, height:34 }}>
                  {initials(squadUser.memberName)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:560, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {squadUser.memberName}
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:1 }}>
                    {squadUser.memberRole} • {squadUser.memberEmail?.split('@')[0]}
                  </div>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Column */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

          {/* Header */}
          <header className="fl-header">
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="fl-ibtn fl-hamburger">
                    <Menu size={16} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" style={{ width:252, padding:0, background:'var(--c)', border:'none', borderRight:'1px solid var(--c3)' }}>
                  <div style={{ height:60, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 18px', borderBottom:'1px solid var(--c3)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <ForgeMark />
                      <span style={{ fontFamily:"'Lora',serif", fontWeight:600, fontSize:14, color:'var(--forest)' }}>The Forge</span>
                    </div>
                    <SheetClose asChild>
                      <button className="fl-ibtn"><X size={14} /></button>
                    </SheetClose>
                  </div>
                  <div className="fl-scroll" style={{ overflowY:'auto', height:'calc(100% - 60px)' }}>
                    <NavLinks onNavigate={() => setMobileOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>

              <span className="fl-mobile-title" style={{ fontFamily:"'Lora',serif", fontWeight:600, fontSize:15, color:'var(--forest)' }}>
                The Forge
              </span>
              
              {/* Show logged-in member on desktop */}
              {squadUser && (
                <span className="hidden md:inline text-xs text-muted-foreground ml-2">
                  Logged in as: {squadUser.memberName} ({squadUser.memberRole})
                </span>
              )}
            </div>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="fl-av" style={{ width:34, height:34, fontSize:13 }}>
                  {squadUser ? initials(squadUser.memberName) : <User size={14} />}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ width:220, padding:6, background:'var(--white)', border:'1px solid var(--c3)', borderRadius:13, boxShadow:'0 10px 40px rgba(28,56,48,.10)' }}>
                {squadUser && (
                  <div style={{ padding:'10px 12px 13px', borderBottom:'1px solid var(--c3)', marginBottom:6 }}>
                    <div style={{ fontFamily:"'Lora',serif", fontWeight:600, fontSize:14, color:'var(--forest)', lineHeight:1.3 }}>
                      {squadUser.memberName}
                    </div>
                    <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:2 }}>{squadUser.memberEmail}</div>
                    <span style={{ display:'inline-block', marginTop:7, fontSize:10.5, fontWeight:650, color:'var(--gold)', background:'var(--gold-bg)', borderRadius:5, padding:'2px 7px' }}>
                      {squadUser.memberRole}
                    </span>
                  </div>
                )}
                <div style={{ height:1, background:'var(--c3)', margin:'2px 4px 6px' }} />
                <DropdownMenuItem className="fl-di red" onClick={handleLogout}>
                  <LogOut size={14} strokeWidth={1.7} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          {/* Content */}
          <main style={{ flex:1, overflowY:'auto', background:'var(--c)', padding:'clamp(16px, 3vw, 40px)' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}